/**
 * Wire → view-model mapping for the reference ledger (WP-F8a).
 *
 * Kept pure and transport-free so the mapping — the crux of the ledger — is unit-tested directly
 * without a fetch. The screen renders `LedgerCard`s; the api-client seam is what turns the wire
 * (`LedgerItemResponse`, or the mock's fixtures) into them.
 *
 * The candidate-facing states are the seven from design-spec/05 Screen 01 + ux-flows §1.C:
 *   Sent · Opened · Recording (in-progress, live) · Ready·Locked · Letter (unlocked) · Declined · Expired
 *
 * Two contract facts pinned here:
 *  - **The active pipeline band collapses to one candidate-facing state.** design-spec/08 is
 *    explicit: `recording | processing | awaiting_sign` "all read as in-progress to the candidate
 *    (only 'Recording' is surfaced live in mocks)". So `accepted`, `recorded`, `processing`,
 *    `drafted`, `needs_review`, `rerecord_requested` and `failed` all map to the single
 *    `in_progress` card. The candidate never sees pipeline granularity — WP-F8b's request-detail
 *    timeline is where the finer status is surfaced.
 *  - **`unlocked` (the "Letter" card) is NOT derivable from the ledger wire.** Unlock is an
 *    entitlement (server-side, permanent, per candidate × letter — design-spec/08 "Entitlements").
 *    Even after WP-F11a wired the real unlock rails, the `LedgerItemResponse` carries no entitlement
 *    field and the status machine tops out at `signed`. So a real signed reference maps to `ready`
 *    (Ready·Locked) here, always — the mapper never fabricates an `unlocked`. The entitlement is read
 *    on the request-detail screen from the unlock-options endpoint (`GET …/unlock`), never from this
 *    ledger row; the `unlocked` view-model state is rendered there (and the mock seeds one so all
 *    seven states are demoable + tested). See DESIGN-NOTES.md.
 */
import type { LedgerItemResponse } from './wire';

/** The full `reference_requests.status` machine, snake_case (matches the DB + generated api-types). */
export type ReferenceStatus =
  | 'draft'
  | 'requested'
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'recorded'
  | 'processing'
  | 'drafted'
  | 'needs_review'
  | 'rerecord_requested'
  | 'failed'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'cancelled';

/** The candidate-facing ledger card states (the seven from design-spec/05 Screen 01). */
export type LedgerCardState =
  | 'sent'
  | 'opened'
  | 'in_progress'
  | 'ready'
  | 'unlocked'
  | 'declined'
  | 'expired';

/** A single ledger row, as the screen renders it. */
export interface LedgerCard {
  id: string;
  refereeName: string;
  company: string | null;
  /** The raw wire status, kept for the request-detail deep link (WP-F8b) and debugging. */
  status: string;
  state: LedgerCardState;
  requestedAt: string;
  sentAt: string | null;
  respondedAt: string | null;
  signedAt: string | null;
  expiresAt: string;
  closedAt: string | null;
}

/**
 * The candidate-facing state for a wire status, or `null` for rows that are intentionally NOT shown
 * in the ledger:
 *  - `cancelled` — the candidate withdrew it themselves; terminal and self-initiated, with no
 *    design card. Filtered rather than mislabelled as "Declined" (which would blame the referee).
 *  - anything unrecognised — degrade by hiding, never by guessing a bucket. The status set is a
 *    closed CHECK-constrained enum, so this is defensive; if the backend adds a state, it stays
 *    invisible until the mapper learns it, which is safer than rendering an affordance we can't
 *    reason about (the same conservative stance the admin console takes on unknown values).
 */
export function stateForStatus(status: string): LedgerCardState | null {
  switch (status as ReferenceStatus) {
    case 'draft':
    case 'requested':
    case 'sent':
      return 'sent';
    case 'viewed':
      return 'opened';
    case 'accepted':
    case 'recorded':
    case 'processing':
    case 'drafted':
    case 'needs_review':
    case 'rerecord_requested':
    case 'failed':
      return 'in_progress';
    case 'signed':
      return 'ready';
    case 'declined':
      return 'declined';
    case 'expired':
      return 'expired';
    case 'cancelled':
      return null;
    default:
      return null;
  }
}

/** Map one wire row to a card, or `null` when the row is not shown (see {@link stateForStatus}). */
export function toLedgerCard(wire: LedgerItemResponse): LedgerCard | null {
  const state = stateForStatus(wire.status);
  if (state === null) return null;
  return {
    id: wire.id,
    refereeName: wire.refereeName,
    company: wire.refereeCompany,
    status: wire.status,
    state,
    requestedAt: wire.requestedAt,
    sentAt: wire.sentAt,
    respondedAt: wire.respondedAt,
    signedAt: wire.signedAt,
    expiresAt: wire.expiresAt,
    closedAt: wire.closedAt,
  };
}

/** Map a wire array to cards, dropping the hidden ones. Server order is preserved (newest-first). */
export function toLedgerCards(wire: LedgerItemResponse[]): LedgerCard[] {
  return wire.map(toLedgerCard).filter((c): c is LedgerCard => c !== null);
}

/** True when at least one reference is Ready·Locked — gates the gold summary strip. */
export function hasReady(cards: readonly LedgerCard[]): boolean {
  return cards.some((c) => c.state === 'ready');
}

/** How many references are Ready·Locked (drives the summary-strip count copy). */
export function readyCount(cards: readonly LedgerCard[]): number {
  return cards.filter((c) => c.state === 'ready').length;
}

/**
 * The status chip label per state. Design canon (04-copy "Statuses"): the active band is surfaced
 * as `Recording`; `ready` is `Unlock`, `unlocked` is `Letter`. `Expired` has no canonical chip
 * string in 04-copy — it is named in ux-flows §1.C + design-spec/08, and the neutral label is used
 * (see DESIGN-NOTES).
 */
export function chipLabelFor(state: LedgerCardState): string {
  switch (state) {
    case 'sent':
      return 'Sent';
    case 'opened':
      return 'Opened';
    case 'in_progress':
      return 'Recording';
    case 'ready':
      return 'Unlock';
    case 'unlocked':
      return 'Letter';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
  }
}

/** The status-dot kind paired with the meta line (never colour alone — always beside a label). */
export type DotKind = 'wait' | 'open' | 'live' | 'done' | 'none';

export interface CardMeta {
  text: string;
  dot: DotKind;
}

/**
 * The meta line + dot for a card. Meta phrases are the sanctioned ones from 04-copy ("Meta lines")
 * where they exist. `now` is injected so the relative-time output is deterministic in tests.
 */
export function metaFor(card: LedgerCard, now: Date = new Date()): CardMeta {
  switch (card.state) {
    case 'sent':
      return { text: `sent ${formatRelative(card.sentAt ?? card.requestedAt, now)}`, dot: 'wait' };
    case 'opened':
      // The wire carries no "openedAt", so no precise relative time is claimed (04-copy "opened ·
      // 12 min" would be invented); the static sanctioned phrase is used instead.
      return { text: 'opened your request', dot: 'open' };
    case 'in_progress':
      return { text: 'live now', dot: 'live' };
    case 'ready':
      return { text: 'recorded a reference', dot: 'none' };
    case 'unlocked':
      return { text: 'yours forever', dot: 'done' };
    case 'declined':
      // Rendered by the card as the "ask another ›" link — no dot.
      return { text: 'ask another', dot: 'none' };
    case 'expired':
      return { text: 'invite expired', dot: 'wait' };
  }
}

/** Which avatar gradient a card gets (design-spec/01 "Avatars" — muted = unopened/declined). */
export type AvatarKind = 'muted' | 'sage' | 'coral' | 'plum' | 'deep-sage';

export function avatarKindFor(state: LedgerCardState): AvatarKind {
  switch (state) {
    case 'opened':
      return 'sage';
    case 'in_progress':
      return 'coral';
    case 'ready':
      return 'plum';
    case 'unlocked':
      return 'deep-sage';
    case 'sent':
    case 'declined':
    case 'expired':
      return 'muted';
  }
}

/** The single visually elevated (gold) card per list — only Ready·Locked earns it. */
export function isElevated(state: LedgerCardState): boolean {
  return state === 'ready';
}

/**
 * Two-letter avatar initials from a display name (first + last word, else first two letters).
 * Null/blank in → `?` out — never throws, so a partial row degrades rather than crashing the list.
 */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Coarse, warm relative time for a ledger meta line. Deliberately imprecise past a week (the exact
 * minute stops mattering) — falls back to a short NZ-formatted date. Bad/blank input degrades to
 * an empty string rather than "Invalid Date".
 */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso);
  const ms = then.getTime();
  if (Number.isNaN(ms)) return '';
  const diffMin = Math.floor((now.getTime() - ms) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'short' }).format(then);
}
